import React, { useEffect, useState } from 'react';
import {
    IMenuItem,
    mainMenuTypes,
    IModalData,
    ModalComponent,
    PasslyTable
} from '@app/components';
import {
    getMenuItemById,
    infiniteScrollItemsFetchLimit,
    fromNow,
    genericErrorToast,
    confirmQuestionData
} from '@app/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { resetScroll, setExceptionHandler } from '@app/store/actions';
import {
    Order,
    OrderType,
    IListPageBarEnabled,
    IPasswordReminders,
    ITableHead,
    VaultPasswordExpireReason,
    FilterEnum, IActiveFilter
} from '@app/models';
import { vaultPasswordsService, basicService } from '@app/services';
import { TableCell } from '@material-ui/core';
import {
    PasslyTooltip
} from '@app/componentsV2';
import { Warning } from '@material-ui/icons';
import { PasswordDetailsDialog } from './dialogs/passwordDetails.dialog';
import { UpdatePasswordDialog } from './dialogs/updatePassword.dialog';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';

interface ITableData {
    id: string;
    passwordName: string;
    passwordId: string;
    vaultName: string;
    vaultId: string;
    folderId: string;
    folderName: string;
    expireAt: string;
    agentProfileId: string;
    revealedAt: string;
    isSynced: boolean;
    isExpired: boolean;
    failedSync: boolean;
    infoButton: string;
}

enum TableHeadEnum {
    passwordName = 'passwordName',
    vaultName = 'vaultName',
    expireAt = 'expireAt',
    syncSource = 'agentProfileId',
    expirationReason = 'revealedAt',
    infoButton = 'infoButton',
    manualUpdateOnly = 'manualUpdateOnly',
}

const PasswordRemindersPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.PasswordReminders);
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('expireAt');
    const [rows, setRows] = useState<ITableData[]>([]);
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [orgAllowedToViewThisPage, setOrgAllowedToViewThisPage] = useState<boolean>(true);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [remindersTotalCount, setRemindersTotalCount] = useState(0);
    const [filters, setFilters] = useState<IActiveFilter[]>([]);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const [openUpdatePasswordModal, setUpdatePasswordModal] = useState(false);
    const [resetScrollCount, setResetScrollCount] = useState(0);

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: false
    };

    const getComplexCellExpireAt = (row: ITableData) => {
        return (
            <TableCell title={translation('UPDATE_NOW')} size='small' onClick={updatePassword(row.passwordId, row.passwordName, row.vaultId)}>
                {row.isExpired ? (
                    <>
                        <span className='vault__passwords--warning'>
                            <Warning fontSize='small' />
                            {translation('ALREADY_EXPIRED')}
                        </span>
                    </>
                ) : (
                    <span>{row.expireAt}</span>
                )}
            </TableCell>
        );
    };
    const getComplexCellInfoButton = (row: ITableData) => {
        return (
            <TableCell>
                <>{getInformationalButton(row)}</>
            </TableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.passwordName,
            label: 'PASSWORD_NAME',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.passwordName,
        },
        {
            id: TableHeadEnum.vaultName,
            label: 'VAULT_NAME',
            filterType: FilterEnum.contains
        },
        {
            id: TableHeadEnum.expireAt,
            label: 'PASSWORD_SERVER_POLICY_DAYS_TO_EXPIRATION',
            type: 'getComplexCell',
            getComplexCell: getComplexCellExpireAt
        },
        {
            id: TableHeadEnum.syncSource,
            label: 'SYNC_SOURCE'
        },
        {
            id: TableHeadEnum.expirationReason,
            label: 'EXPIRATION_REASON'
        },
        {
            id: TableHeadEnum.infoButton,
            label: '',
            type: 'getComplexCell',
            getComplexCell: getComplexCellInfoButton
        },
        {
            id: TableHeadEnum.manualUpdateOnly,
            label: 'ONLY_MANUAL_UPDATE',
            type: 'text',
            filterType: FilterEnum.manualUpdateOnly,
            suggestionsForFilter: confirmQuestionData,
            hidden: true
        },
        {
            id: TableHeadEnum.expireAt,
            label: 'ONLY_EXPIRED',
            type: 'text',
            filterType: FilterEnum.expiredOnly,
            suggestionsForFilter: confirmQuestionData,
            hidden: true
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(setExceptionHandler(true));
    }, []);

    useEffect(() => {
        if (!loadingData) {
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [filters]);

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        vaultPasswordsService.getPasswordReminders(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            organizationFilter,
            handleError,
            handleSuccess,
            isReset,
            true,
            filters
        );
    };

    const handleSuccess = (response: any) => {
        // if it didn't throw an error then it is true
        setOrgAllowedToViewThisPage(true);

        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setRemindersTotalCount(count);
        }

        const results: IPasswordReminders[] = response.data.value;

        if (results) {
            let newRows = results.map((row: IPasswordReminders) => {
                if (row.PasswordType === 'SNMPV3') {
                    if (row.ExpireAt2 !== null) {
                        row.ExpireAt = row.ExpireAt < row.ExpireAt2 ? row.ExpireAt : row.ExpireAt2;
                    }
                    if (row.RevealedAt2 !== null) {
                        row.RevealedAt = row.RevealedAt < row.RevealedAt2 ? row.RevealedAt : row.RevealedAt2;
                    }
                }

                let daysToExpire: string = '';
                let expired: boolean = false;
                const currentDate = new Date();

                if (row.ExpireAt !== null) {
                    daysToExpire = fromNow(row.ExpireAt);
                    expired = new Date(row.ExpireAt) <= new Date(currentDate);
                }

                let source: string = translation('SYNC_SOURCE_LOCAL');
                let synced: boolean = false;
                if (row.AgentProfileId !== null) {
                    source = translation('SYNCED');
                    synced = true;
                }

                let reason: string = translation('EXPIRED');
                switch (row.Reason) {
                    case VaultPasswordExpireReason.Revealed:
                        reason = translation('VIEWED');
                        break;
                    case VaultPasswordExpireReason.Compromised:
                        reason = translation('COMPROMISED');
                        break;
                    default:
                        reason = translation('EXPIRED');
                        break;
                }

                const data: ITableData = {
                    id: row.Id,
                    passwordName: row.PasswordName,
                    vaultName: row.VaultName,
                    expireAt: daysToExpire,
                    agentProfileId: source,
                    revealedAt: reason,
                    isSynced: synced,
                    isExpired: expired,
                    vaultId: row.VaultId,
                    passwordId: row.VaultPasswordId,
                    folderName: row.FolderName,
                    folderId: row.FolderId,
                    failedSync: row.FailedSync,
                    infoButton: ''
                };

                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const handleError = (error: any) => {
        setLoadingData(false);
        setRows([]);

        if (error.response.status === 400 && error.response.data.error.message === 'ORG_CANNOT_VIEW') {
            setOrgAllowedToViewThisPage(false);
        } else if (error.response.status !== 403) {
            // we want to ignore 403 errors for toasting since the page already displays a message
            genericErrorToast(error);
        }
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;

        setOrder(ascDesc);
        setOrderBy(property);
        setLoadingData(true);
        setRows([]);
        dispatch(resetScroll());
    };

    const openPasswordReminderDetails = (row: ITableData) => () => {
        setUpdatePasswordModal(true);
        setModalData({
            headerTitle: row.passwordName,
            modalSwitcher: modalSwitchState,
            form: <PasswordDetailsDialog passwordId={row.passwordId} vaultId={row.vaultId} />
        });
    };

    const handleModalCloseCallback = (close?: boolean) => {
        if (close !== undefined) {
            setUpdatePasswordModal(false);
        } else {
            setUpdatePasswordModal(!openUpdatePasswordModal);
        }
    };

    const updatePassword = (id: string, passwordName: string, vaultId: string) => () => {
        setUpdatePasswordModal(true);
        setModalData({
            headerTitle: translation('UPDATE_PWD_FOR') + ' ' + passwordName,
            modalSwitcher: modalSwitchState,
            form: (
                <UpdatePasswordDialog
                    passwordId={id}
                    vaultId={vaultId}
                    handleRefresh={handlePageRefreshFromDialog}
                    dialogOpen={handleModalCloseCallback}
                />
            )
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const goToPassword = (row: any) => () => {
        basicService.redirect(`/passwordserver/vaults/${row.vaultId}/editpassword/${row.passwordId}?vaultName=${encodeURIComponent(row.vaultName)}`, true);
    };

    const getInformationalButton = (row: ITableData) => {
        if (row.isSynced && row.failedSync) {
            return (
                <PasslyTooltip title={translation('SYNC_FAILED')}>
                    <span className='label__error' onClick={() => resyncPassword(row.vaultId, row.passwordId)}>
                        <Warning fontSize='small' />
                        {translation('RESYNC')}
                    </span>
                </PasslyTooltip>
            );
        } else if (!row.isSynced) {
            return (
                <span className='label__important' onClick={updatePassword(row.passwordId, row.passwordName, row.vaultId)}>
                    <Warning fontSize='small' />
                    {translation('MANUAL_UPDATE')}
                </span>
            );
        } else {
            return <span> </span>;
        }
    };

    const resyncPassword = (vaultId: string, passwordId: string) => {
        let json: any = {};
        json.VaultPasswordId = passwordId;

        vaultPasswordsService.removeFailedSyncFlag(json, vaultId, handleSuccessResync.bind(null, passwordId), genericErrorToast);
    };

    const handleSuccessResync = (passwordId: string) => {
        dispatch(
            handleSetSnackbar({
                show: true,
                message: translation('RESYNC_IN_PROGRESS'),
                severity: 'success'
            })
        );

        // remove retry button
        let newRows = [...rows];

        const rowChanged: ITableData[] = newRows.filter((row: ITableData) => {
            return row.passwordId === passwordId;
        });

        rowChanged[0].failedSync = false;
        setRows(newRows);
    };

    return (
        <>
            <ModalComponent localModalSwitcher={openUpdatePasswordModal} modalSwitcherCallback={handleModalCloseCallback} {...modalData} />

            {orgAllowedToViewThisPage ? (
                <PasslyTable
                    rows={rows}
                    order={order}
                    orderBy={orderBy}
                    handleOrderChange={createSortHandler}
                    headerItems={headCells}
                    rowsCount={remindersTotalCount}
                    onFetch={fetchMoreData}
                    withCheckboxes={false}
                    emptyListHelper={translation('EMPTY_VAULT_PASSWORD_REMINDER_LIST')}
                    loadingData={loadingData}
                    setFilters={setFilters}
                    openDetails={openPasswordReminderDetails}
                    openInNewTab={goToPassword}
                    resetLocal={resetScrollCount}
                />
            ) : (
                <div className='table--empty'>{translation('ORG_CANNOT_VIEW')}</div>
            )}
        </>
    );
};

export default PasswordRemindersPage;
